import { Button, Form, FormTitle, message, Modal, Select, SubContent } from '@maxtropy/components';
import { Col, Row, Space, Table, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { TreeSelect } from 'antd';
import type { TreeSelectProps } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styles from './index.module.scss';
import {
  apiV2ComprehensiveLabelPagePost,
  apiV2EnergyAnalysisListByOuPost,
  apiV2UetEmtProcessListPost,
  apiV2UetTopologyPost,
  V2ComprehensiveLabelPagePostResponse,
  V2UetEmtProcessListPostResponse,
  V2UetTopologyPostResponse,
  apiV2AreaTreePost,
  apiV2AreaGetConfigDetailByAreaIdPost,
} from '@maxtropy/device-customer-apis-v2';
import { fetchUseGasConfigDetail, onFinish } from './util';
import { MediumItem, RegionConfig } from './type';
import EnergyFlowRow from './components/EnergyFlowRow';
import { AnalysisUnit } from '../BindUnitModal';

interface AreaTreeNode {
  id?: number;
  zoneName?: string;
  level?: number;
  gasSupplyPipeline?: string;
  energyUnitId?: number;
  children?: AreaTreeNode[];
}

const UseGasConfig = () => {
  const [form] = Form.useForm();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [unitGroup, setUnitGroup] = useState<AnalysisUnit[]>([]);
  const [deviceLabels, setDeviceLabels] = useState<V2ComprehensiveLabelPagePostResponse['list']>([]);
  const [uetTopology, setUetTopology] = useState<V2UetTopologyPostResponse['list']>([]);
  const [nodeSelect, setNodeSelect] = useState<V2UetEmtProcessListPostResponse['processVos']>([]);
  const watchedTopology = Form.useWatch('energyTopology', form);

  // 区域配置
  const [regionConfigs, setRegionConfigs] = useState<RegionConfig[]>([]);
  const [treeNodeData, setTreeNodeData] = useState<TreeSelectProps['treeData']>([]);
  const [areaTreeData, setAreaTreeData] = useState<AreaTreeNode[]>([]);
  const [areaMediumCache, setAreaMediumCache] = useState<Record<number, MediumItem[]>>({});
  const [saveLoading, setSaveLoading] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(true);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true);
  const [nodeLoading, setNodeLoading] = useState<boolean>(false);
  const [childLoadingCount, setChildLoadingCount] = useState<number>(0);

  const handleChildLoadingChange = useCallback((loading: boolean) => {
    setChildLoadingCount(count => Math.max(loading ? count + 1 : count - 1, 0));
  }, []);

  // 数据回显
  useEffect(() => {
    setDetailLoading(true);
    fetchUseGasConfigDetail(form, () => {}, setRegionConfigs, setAreaMediumCache)
      .then(res => {
        console.log('用气配置详情:', res);
      })
      .catch(() => undefined)
      .finally(() => setDetailLoading(false));
  }, [refresh]);

  // 获取区域树
  const formatAreaTreeData = (data: AreaTreeNode[]): TreeSelectProps['treeData'] => {
    return data.map(item => ({
      title: item.zoneName,
      value: item.id,
      label: item.zoneName,
      children: item.children ? formatAreaTreeData(item.children) : [],
    }));
  };

  const getAreaTree = () => {
    handleChildLoadingChange(true);
    apiV2AreaTreePost({})
      .then(data => {
        const treeList = (data.list || []) as AreaTreeNode[];
        setAreaTreeData(treeList);
        setTreeNodeData(formatAreaTreeData(treeList));
      })
      .catch(() => undefined)
      .finally(() => handleChildLoadingChange(false));
  };

  const findAreaNode = (nodes: AreaTreeNode[], areaId?: number): AreaTreeNode | undefined => {
    for (const node of nodes) {
      if (node.id === areaId) return node;
      const childNode = findAreaNode(node.children ?? [], areaId);
      if (childNode) return childNode;
    }
    return undefined;
  };

  useEffect(() => {
    getAreaTree();
  }, []);

  // 获取区域对应的能源介质列表
  const fetchAreaMediums = async (areaId: number): Promise<MediumItem[]> => {
    if (areaMediumCache[areaId]) {
      return areaMediumCache[areaId];
    }
    handleChildLoadingChange(true);
    try {
      const res = await apiV2AreaGetConfigDetailByAreaIdPost({ areaId });
      const mediums: MediumItem[] = (res.timePeriodList || [])
        .filter((item): item is { mediumId: number; mediumName: string } => {
          return item.mediumId !== undefined && item.mediumName !== undefined;
        })
        .map(item => ({
          mediumId: item.mediumId,
          mediumName: item.mediumName,
        }));
      setAreaMediumCache(prev => ({ ...prev, [areaId]: mediums }));
      return mediums;
    } finally {
      handleChildLoadingChange(false);
    }
  };

  useEffect(() => {
    setOptionsLoading(true);
    Promise.all([
      apiV2EnergyAnalysisListByOuPost({}).then(res => {
        setUnitGroup((res.list ?? []) as AnalysisUnit[]);
      }),
      apiV2ComprehensiveLabelPagePost({ page: 1, size: 100, type: 3 }).then(res => {
        setDeviceLabels(res.list || []);
      }),
      apiV2UetTopologyPost({}).then(res => {
        setUetTopology(res.list || []);
      }),
    ])
      .catch(() => undefined)
      .finally(() => setOptionsLoading(false));
  }, []);

  useEffect(() => {
    if (watchedTopology) {
      setNodeLoading(true);
      apiV2UetEmtProcessListPost({ id: watchedTopology })
        .then(res => {
          setNodeSelect(res.processVos || []);
        })
        .catch(() => undefined)
        .finally(() => setNodeLoading(false));
    } else {
      setNodeSelect([]);
    }
  }, [watchedTopology]);

  // 区域配置操作
  const handleAddRegion = () => {
    setRegionConfigs(prev => [...prev, { key: uuidv4(), areaName: undefined, areaId: undefined, mediums: [] }]);
  };

  const handleDeleteRegion = (key: string) => {
    setRegionConfigs(prev => prev.filter(item => item.key !== key));
  };

  const handleRegionAreaChange = async (key: string, value: number, label: string) => {
    // 清空该区域的介质选择
    setRegionConfigs(prev =>
      prev.map(item => (item.key === key ? { ...item, areaId: value, areaName: label, mediums: [] } : item))
    );
    // 获取该区域的能源介质列表并缓存
    if (value) {
      await fetchAreaMediums(value);
    }
  };

  const handleRegionMediumChange = (key: string, mediumIds: number[], areaId: number | undefined) => {
    const availableMediums = areaId ? areaMediumCache[areaId] || [] : [];
    const mediums: MediumItem[] = mediumIds.map(id => {
      const found = availableMediums.find(m => m.mediumId === id);
      return { mediumId: id, mediumName: found?.mediumName ?? '' };
    });
    setRegionConfigs(prev => prev.map(item => (item.key === key ? { ...item, mediums } : item)));
  };

  const regionColumns: ColumnsType<RegionConfig> = [
    {
      title: '区域名称',
      dataIndex: 'areaName',
      width: 350,
      render: (_, record) => (
        <TreeSelect
          disabled={!isEdit}
          treeData={treeNodeData}
          style={{ width: '100%' }}
          allowClear
          showSearch
          treeNodeFilterProp="title"
          placeholder="请选择区域"
          value={record.areaId}
          onChange={value => {
            const label = findAreaNode(areaTreeData, value as number)?.zoneName ?? '';
            handleRegionAreaChange(record.key, value as number, label);
          }}
        />
      ),
    },
    {
      title: '能源介质',
      dataIndex: 'mediums',
      render: (_, record) => {
        const selectedIds = record.mediums.map(m => m.mediumId);
        const availableMediums = record.areaId ? areaMediumCache[record.areaId] || [] : [];
        return (
          <Select
            disabled={!isEdit || !record.areaId}
            mode="multiple"
            style={{ width: '100%' }}
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder={!record.areaId ? '请先选择区域' : '请选择能源介质'}
            options={availableMediums.map(m => ({ label: m.mediumName, value: m.mediumId }))}
            value={selectedIds}
            onChange={value => handleRegionMediumChange(record.key, value, record.areaId)}
          />
        );
      },
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          danger
          disabled={!isEdit}
          onClick={() => handleDeleteRegion(record.key)}
          style={{ padding: 0 }}
        >
          删除
        </Button>
      ),
    },
  ];

  return (
    <Spin
      spinning={detailLoading || optionsLoading || nodeLoading || saveLoading || childLoadingCount > 0}
      wrapperClassName={styles.pageSpin}
    >
      <Form
        form={form}
        layout="horizontal"
        onFinish={values => {
          setSaveLoading(true);
          onFinish(
            values,
            regionConfigs,
            unitGroup as { name: string; id: number }[],
            setIsEdit,
            setRefresh,
            message
          ).finally(() => setSaveLoading(false));
        }}
      >
        <div style={{ padding: 20 }}>
          <FormTitle title="用气监控配置"></FormTitle>
          <SubContent title="全厂用气监控配置">
            <div style={{ marginBottom: 12 }}>
              <Button type="primary" disabled={!isEdit} onClick={handleAddRegion}>
                选择区域
              </Button>
            </div>
            <Table
              rowKey="key"
              columns={regionColumns}
              dataSource={regionConfigs}
              pagination={false}
              style={{ maxWidth: 700 }}
            />
          </SubContent>
          <SubContent title="用气能流配置">
            <div style={{ marginBottom: '6px' }}>
              <Row gutter={44}>
                <Col flex="0 0 345px">
                  <div className={styles.energyAnalysisGroupTitle}>用能分析组选择</div>
                </Col>
                <Col flex="0 0 345px">
                  <div className={styles.energyAnalysisGroupTitle}>展示层级</div>
                </Col>
              </Row>
            </div>
            <Row gutter={44}>
              {[1, 2, 3].map(index => (
                <EnergyFlowRow
                  key={index}
                  index={index}
                  isEdit={isEdit}
                  unitGroup={unitGroup}
                  onLoadingChange={handleChildLoadingChange}
                />
              ))}
            </Row>
          </SubContent>
          <SubContent title="偏差分析配置">
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  name="energyTopology"
                  label={<span style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>能源介质拓扑选择</span>}
                  rules={[{ required: true, message: '请选择能源介质拓扑' }]}
                >
                  <Select
                    disabled={!isEdit}
                    placeholder="请选择"
                    options={uetTopology?.map(i => ({ label: i.name, value: i.id }))}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    onChange={() => {
                      form.setFieldValue('pipeLossNode', []); // 拓扑变化时清空节点选择
                    }}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="pipeLossNode" label="节点选择：" style={{ flex: 0.77 }} rules={[{ required: true }]}>
                  <Select
                    disabled={!isEdit}
                    placeholder="请选择"
                    options={nodeSelect?.map(i => ({ label: i.name, value: i.id }))}
                    showSearch
                    mode="multiple"
                    allowClear
                    maxTagCount={3}
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
            </Row>
          </SubContent>
          <SubContent title="报警统计配置">
            <Form.Item name="deviceLabel" label="设备标签" rules={[{ required: true }]}>
              <Select
                placeholder="请选择设备标签"
                options={deviceLabels?.map(i => ({ label: i.name, value: i.id }))}
                showSearch
                optionFilterProp="label"
                mode="multiple"
                disabled={!isEdit}
                style={{ maxWidth: '472px' }}
                allowClear
              />
            </Form.Item>
          </SubContent>
        </div>
      </Form>
      <Space className="sticky-footer" size={8}>
        {!isEdit ? (
          <>
            <Button type="primary" onClick={() => setIsEdit(true)}>
              编辑
            </Button>
          </>
        ) : (
          <Space>
            <Button type="primary" onClick={() => form.submit()}>
              保存
            </Button>
            <Button
              onClick={() => {
                if (isEdit) {
                  Modal.confirm({
                    title: '是否放弃所有未保存信息？',
                    onOk: () => {
                      form.resetFields();
                      setRefresh(prev => !prev);
                      setIsEdit(false);
                    },
                  });
                }
              }}
            >
              取消
            </Button>
          </Space>
        )}
      </Space>
    </Spin>
  );
};

export default UseGasConfig;
